// src/Components/Modules/HR/StaffDeployments.js
import React, { useState, useEffect } from 'react';
import { AxiosAuthInstance } from '../../../AxiosInterceptors';
import Breadcrumb from '../../Partials/Breadcrumb';
import { useNavigate } from "react-router-dom";
import {
    Box, Typography, Paper, Button, TextField, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Dialog, DialogTitle, DialogContent,
    DialogActions, Chip, FormControl, InputLabel, Select, MenuItem, IconButton
} from '@mui/material';
import { FaPlus, FaFileInvoice, FaCheck, FaEye, FaTrash, FaUsers, FaEdit } from 'react-icons/fa';
import Swal from 'sweetalert2';

export default function StaffDeployments() {
    const navigate = useNavigate();
    const [deployments, setDeployments] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null); 
    
    const [form, setForm] = useState({
        customer_id: '', service_date: new Date().toISOString().split('T')[0],
        end_date: '', location_address: '', project_name: '',
        assignments: [{ employee_id: '', service_date: new Date().toISOString().split('T')[0], hours_worked: '', hourly_rate: '' }]
    });

    const fetchData = async () => {
        try {
            const [dRes, eRes, cRes] = await Promise.all([
                AxiosAuthInstance.get('staff-deployments'),
                AxiosAuthInstance.get('add_employee'),
                AxiosAuthInstance.get('add_customer')
            ]);
            setDeployments(dRes.data.deployments || []);
            setEmployees(Array.isArray(eRes.data.employee) ? eRes.data.employee : []);
            setCustomers(cRes.data.customers || cRes.data || []);
        } catch (err) { console.error("Failed to load data", err); }
    };

    useEffect(() => { fetchData(); }, []);

    const handleAddAssignmentRow = () => {
        setForm({ ...form, assignments: [...form.assignments, { employee_id: '', service_date: form.service_date, hours_worked: '', hourly_rate: '' }] });
    };

    const handleRemoveAssignmentRow = (index) => {
        const newAssignments = form.assignments.filter((_, i) => i !== index);
        setForm({ ...form, assignments: newAssignments });
    };

    const handleAssignmentChange = (index, field, value) => {
        const newAssignments = [...form.assignments];
        newAssignments[index][field] = value;
        setForm({ ...form, assignments: newAssignments });
    };

    const calculateTotal = () => {
        return form.assignments.reduce((sum, item) => sum + (parseFloat(item.hours_worked || 0) * parseFloat(item.hourly_rate || 0)), 0);
    };

    const handleEdit = (deployment) => {
        setEditing(deployment);
        setForm({
            customer_id: deployment.customer_id,
            service_date: deployment.service_date ? deployment.service_date.split('T')[0] : new Date().toISOString().split('T')[0],
            end_date: deployment.end_date ? deployment.end_date.split('T')[0] : '',
            location_address: deployment.location_address,
            project_name: deployment.project_name || '',
            assignments: deployment.assignments.map(a => ({
                employee_id: a.employee_id,
                service_date: a.service_date ? a.service_date.split('T')[0] : new Date().toISOString().split('T')[0],
                hours_worked: a.hours_worked,
                hourly_rate: a.hourly_rate
            }))
        });
        setDialogOpen(true);
    };

    const handleSave = async () => {
        if (form.assignments.some(a => !a.employee_id || !a.hours_worked || !a.hourly_rate)) {
            Swal.fire({ icon: "warning", title: "Warning", text: "Please fill all employee assignment details." });
            return;
        }
        try {
            if (editing) {
                await AxiosAuthInstance.put(`staff-deployments/${editing.id}`, form);
                Swal.fire({ icon: 'success', title: 'Deployment Updated!', timer: 1500, showConfirmButton: false });
            } else {
                await AxiosAuthInstance.post('staff-deployments', form);
                Swal.fire({ icon: 'success', title: 'Deployment Scheduled!', timer: 1500, showConfirmButton: false });
            }
            
            setDialogOpen(false);
            setEditing(null);
            setForm({
                customer_id: '', service_date: new Date().toISOString().split('T')[0], end_date: '', location_address: '', project_name: '',
                assignments: [{ employee_id: '', service_date: new Date().toISOString().split('T')[0], hours_worked: '', hourly_rate: '' }]
            });
            fetchData();
        } catch (err) {
            Swal.fire({ icon: 'error', title: 'Error', text: err.response?.data?.message || 'Failed' });
        }
    };

    const handleComplete = async (id) => {
        try {
            await AxiosAuthInstance.post(`staff-deployments/${id}/complete`);
            Swal.fire({ icon: 'success', title: 'Completed!', timer: 1500, showConfirmButton: false });
            fetchData();
        } catch (err) {
            Swal.fire({ icon: 'error', title: 'Error', text: err.response?.data?.error || 'Failed' });
        }
    };

    const handleGenerateInvoice = async (id, deployment) => {
        if (deployment.status === 'invoiced' && deployment.billing_id) {
            Swal.fire({
                icon: 'info', title: 'Already Invoiced', text: 'View existing invoice?',
                showCancelButton: true, confirmButtonText: '🖨️ View Invoice', cancelButtonText: 'Close'
            }).then((result) => {
                if (result.isConfirmed) {
                    AxiosAuthInstance.get(`billings/${deployment.billing_id}`)
                        .then(response => navigate("/print-receipt", { state: { billing: response.data.billing || response.data,  deployment: deployment } }))
                        .catch(() => Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to load invoice' }));
                }
            });
            return;
        }

        const res = await Swal.fire({ title: 'Generate Invoice?', text: 'This will bill for all completed staff assignments.', icon: 'question', showCancelButton: true });
        if (res.isConfirmed) {
            try {
                const response = await AxiosAuthInstance.post(`staff-deployments/${id}/generate-invoice`);
                Swal.fire({
                    icon: 'success', title: 'Invoice Generated!', text: `Billing ID: ${response.data.billing.order_number}`,
                    showCancelButton: true, confirmButtonText: '️ Print / Preview', cancelButtonText: 'Close'
                }).then((result) => {
                    if (result.isConfirmed) navigate("/print-receipt", { state: { billing: response.data.billing } });
                });
                fetchData();
            } catch (err) {
                Swal.fire({ icon: 'error', title: 'Error', text: err.response?.data?.error || 'Failed' });
            }
        }
    };

    const formatDate = (dateString) => dateString ? new Date(dateString).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : 'N/A';

    const statusColor = { scheduled: 'warning', completed: 'info', invoiced: 'primary', paid: 'success', cancelled: 'error' };

    return (
        <Box sx={{ p: 3, bgcolor: '#f5f7fa', minHeight: '100vh' }}>
            <Breadcrumb title="Staff Deployments" />
            <Paper sx={{ p: 3, mb: 3, display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="h5" fontWeight={700}>Manpower / Staff Rental</Typography>
                <Button variant="contained" startIcon={<FaPlus />} onClick={() => { setEditing(null); setDialogOpen(true); }}>New Deployment</Button>
            </Paper>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow sx={{ bgcolor: '#f8f9fa' }}>
                            <TableCell>Project / Client</TableCell>
                            <TableCell>Date Range</TableCell>
                            <TableCell>Location</TableCell>
                            <TableCell>Staff Count</TableCell>
                            <TableCell>Total Value</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="center">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {deployments.map((d) => {
                            const staffCount = d.assignments?.length || 0;
                            const totalVal = d.assignments?.reduce((sum, a) => sum + parseFloat(a.total_amount || 0), 0) || 0;
                            return (
                                <TableRow key={d.id} hover>
                                    <TableCell>
                                        <strong>{d.project_name || 'General Deployment'}</strong><br/>
                                        <Typography variant="caption" color="text.secondary">{d.customer?.name || 'Unknown'}</Typography>
                                    </TableCell>
                                    <TableCell>{formatDate(d.service_date)} {d.end_date ? `to ${formatDate(d.end_date)}` : ''}</TableCell>
                                    <TableCell>{d.location_address}</TableCell>
                                    <TableCell><Chip icon={<FaUsers />} label={`${staffCount} Staff`} size="small" color="info" /></TableCell>
                                    <TableCell><strong>৳{Number(totalVal).toLocaleString()}</strong></TableCell>
                                    <TableCell><Chip label={d.status} size="small" color={statusColor[d.status]} /></TableCell>
                                    <TableCell align="center">
                                        {/* ✅ Edit Button (Only for scheduled/completed) */}
                                        {(d.status === 'scheduled' || d.status === 'completed') && (
                                            <Button size="small" variant="outlined" color="warning" startIcon={<FaEdit />} onClick={() => handleEdit(d)} sx={{ mr: 1 }}>
                                                Edit
                                            </Button>
                                        )}
                                        
                                        {d.status === 'scheduled' && (
                                            <Button size="small" variant="outlined" color="success" startIcon={<FaCheck />} onClick={() => handleComplete(d.id)}>Complete</Button>
                                        )}
                                        {(d.status === 'completed' || d.status === 'scheduled' || d.status === 'invoiced') && (
                                            <Button size="small" variant="contained" color={d.status === 'invoiced' ? "info" : "primary"} startIcon={d.status === 'invoiced' ? <FaEye /> : <FaFileInvoice />} onClick={() => handleGenerateInvoice(d.id, d)} sx={{ ml: 1 }}>
                                                {d.status === 'invoiced' ? 'View' : 'Invoice'}
                                            </Button>
                                        )}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog open={dialogOpen} onClose={() => { setDialogOpen(false); setEditing(null); }} maxWidth="md" fullWidth>
                <DialogTitle>{editing ? 'Edit Deployment' : 'Schedule Staff Deployment'}</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Client (Customer)</InputLabel>
                                <Select value={form.customer_id} label="Client" onChange={(e) => setForm({ ...form, customer_id: e.target.value })}>
                                    {customers.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
                                </Select>
                            </FormControl>
                            <TextField label="Project Name (Optional)" size="small" value={form.project_name} onChange={(e) => setForm({ ...form, project_name: e.target.value })} fullWidth />
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <TextField label="Start Date *" type="date" size="small" value={form.service_date} onChange={(e) => setForm({ ...form, service_date: e.target.value })} InputLabelProps={{ shrink: true }} fullWidth required />
                            <TextField label="End Date (Optional)" type="date" size="small" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} InputLabelProps={{ shrink: true }} fullWidth />
                        </Box>
                        <TextField label="Location / Address *" size="small" value={form.location_address} onChange={(e) => setForm({ ...form, location_address: e.target.value })} fullWidth required />
                        
                        <Typography variant="subtitle1" fontWeight={600} sx={{ mt: 1 }}>Staff Assignments</Typography>
                        {form.assignments.map((assign, index) => (
                            <Box key={index} sx={{ display: 'flex', gap: 1, alignItems: 'center', p: 1, bgcolor: '#f8f9fa', borderRadius: 1 }}>
                                <FormControl size="small" sx={{ flex: 2 }}>
                                    <InputLabel>Employee</InputLabel>
                                    <Select value={assign.employee_id} label="Employee" onChange={(e) => handleAssignmentChange(index, 'employee_id', e.target.value)}>
                                        {employees.map(e => (
                                            <MenuItem key={e.id} value={e.id}>
                                                {/* ✅ Name with Designation */}
                                                {e.employee_name} {e.designation || e.designation_name ? `- ${e.designation || e.designation_name}` : ''}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <TextField label="Date *" type="date" size="small" value={assign.service_date} onChange={(e) => handleAssignmentChange(index, 'service_date', e.target.value)} InputLabelProps={{ shrink: true }} sx={{ flex: 1 }} />
                                <TextField label="Hours" type="number" size="small" value={assign.hours_worked} onChange={(e) => handleAssignmentChange(index, 'hours_worked', e.target.value)} sx={{ flex: 0.5 }} />
                                <TextField label="Rate (৳)" type="number" size="small" value={assign.hourly_rate} onChange={(e) => handleAssignmentChange(index, 'hourly_rate', e.target.value)} sx={{ flex: 1 }} />
                                <Typography sx={{ flex: 1, fontWeight: 600, textAlign: 'right' }}>
                                    ৳{((parseFloat(assign.hours_worked) || 0) * (parseFloat(assign.hourly_rate) || 0)).toLocaleString()}
                                </Typography>
                                <IconButton size="small" color="error" onClick={() => handleRemoveAssignmentRow(index)} disabled={form.assignments.length === 1}>
                                    <FaTrash />
                                </IconButton>
                            </Box>
                        ))}
                        <Button size="small" startIcon={<FaPlus />} onClick={handleAddAssignmentRow} sx={{ alignSelf: 'flex-start' }}>Add Another Staff Member</Button>

                        <Box sx={{ p: 2, bgcolor: '#e3f2fd', borderRadius: 1, textAlign: 'center', mt: 1 }}>
                            <Typography variant="body2" color="text.secondary">Estimated Total Project Bill</Typography>
                            <Typography variant="h5" color="primary" fontWeight={700}>৳{calculateTotal().toLocaleString()}</Typography>
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { setDialogOpen(false); setEditing(null); }}>Cancel</Button>
                    <Button variant="contained" onClick={handleSave}>{editing ? 'Update Deployment' : 'Schedule Deployment'}</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}